import { Languages, LocalStorageKeys } from '@/constants'
import { useDebounce } from '@/hooks/useDebounce'
import { type Product, type ProductCompare, type ProductViewed } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { getProductWithPaginate } from '@/utils/endpoint'
import { isNil } from 'lodash'
import { useRouter } from 'next/router'
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import DrawBottom from '../DrawBottom'
import { SearchIconCompare } from '../Icon/HeaderIcon'
import ProductCompareCard from '../ProductCompareCard'
import { SearchProductItemCard } from '../SearchProductItemCard'
export interface ChooseProductCompareMethod {
  className?: string
  isOpen: boolean
  catViewId: string | null
  setIsOpen: Dispatch<SetStateAction<boolean>>
  setIsOpenComparePopup: Dispatch<SetStateAction<boolean>>
  setProductCompares: (newValue: ProductCompare[]) => void
  productCompares: ProductCompare[]
  productViewed: ProductViewed[]
}

export function ChooseProductCompare({
  isOpen,
  setIsOpen,
  catViewId,
  setProductCompares,
  productCompares,
  productViewed,
  setIsOpenComparePopup,
}: ChooseProductCompareMethod) {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState<Product[]>([])
  const [productVieweds, setProductVieweds] = useState<Product[]>([])
  const [searchBarValue, setSearchBarValue] = useState('')
  const debouncedSearchBarValue = useDebounce(searchBarValue, 500)

  const handleInputKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      if (searchBarValue === '') {
        localStorage?.removeItem(LocalStorageKeys.ITEM_LISTING_SEARCH)
      }
    }
    if (event.key === 'Enter') {
      return router.push(`/search?query=${searchBarValue}`)
    }
  }

  useEffect(() => {
    const query = router.query
    if (debouncedSearchBarValue && query) {
      setLoading(true)
      getProductWithPaginate({
        ...query,
        fullTextSearch: debouncedSearchBarValue,
        limit: 3,
        searching: true,
        sortBy: 'score',
        sortType: 'desc',
        ...(catViewId && { catId: catViewId }),
      }).then(res => {
        setProducts(res?.data || [])
        setLoading(false)
      })
    }
  }, [debouncedSearchBarValue])

  useEffect(() => {
    const productViewIds = productViewed.map(e => e._id)
    if (isOpen) {
      getProductWithPaginate({
        fullTextSearch: debouncedSearchBarValue,
        limit: 3,
        sortBy: 'score',
        sortType: 'desc',
        searching: true,
        ...(productViewIds.length > 0 && {
          id: productViewed.map(e => e._id).join(','),
        }),
        ...(catViewId && { catId: catViewId }),
      }).then(res => {
        setProductVieweds(res?.data || [])
        setLoading(false)
      })
    }
  }, [isOpen])

  const handleAddToCompare = (product: Product) => {
    const emptyIndex = productCompares.findIndex(
      e => Object.keys(e).length === 0
    )
    productCompares[emptyIndex > -1 ? emptyIndex : productCompares.length - 1] =
      {
        _id: product._id,
        image: product.thumbnail,
        name: getTranslate(product.name),
        link: getTranslate(product.slugs.value) + product.slugs.postfix,
      }
    setProductCompares([...productCompares])
    setIsOpen(false)
    if (!router.pathname.includes('/compare')) {
      setIsOpenComparePopup(true)
    }
  }

  return (
    <DrawBottom
      mainClassName='!h-[70vh] !m-0 !p-0'
      titleClassName='!h-[50px] border-b border-gray-400'
      open={isOpen}
      bodyClassName='!pr-0 !mr-0 min-h-[calc(70vh-96px)]'
      onClose={() => {
        setIsOpen(false)
      }}
      title={' Thêm sản phẩm so sánh'}
    >
      <div className='listProductRelated mb-10 mt-[10px] flex flex-col'>
        <h3 className='mb-2 text-fieldLabel font-bold text-blue-1100'>
          Sản phẩm đã xem gần đây
        </h3>
        <div className='grid h-full grid-cols-3 gap-x-2 py-[5px]'>
          {productVieweds.length > 0 &&
            productVieweds.map((e, index) => (
              <ProductCompareCard
                handleAddToCompare={() => {
                  handleAddToCompare(e)
                  setIsOpen(false)
                }}
                className={
                  '!h-[227px] rounded border border-grey-1500 px-[8px]'
                }
                imageClassName={'!bg-transparent'}
                key={index}
                product={e}
                layout={3}
                canCompare={isNil(productCompares.find(f => f._id === e._id))}
              />
            ))}
        </div>

        <h3 className='mb-[15px] mt-[20px] text-fieldLabel font-bold text-blue-1100'>
          Hoặc nhập để tìm kiếm
        </h3>
        <div className='flex rounded-lg border border-grey-1500'>
          <span className='start-[-1px] flex h-[48px] min-w-[48px] cursor-pointer items-center justify-center p-0'>
            <SearchIconCompare className='' />
          </span>
          <input
            type='text'
            className={`!h-12 w-full p-3 shadow-none transition hover:cursor-pointer focus:shadow-none focus:outline-none focus:ring-0`}
            placeholder={'Nhập sản phẩm bạn muốn tìm'}
            value={searchBarValue}
            onChange={e => {
              setSearchBarValue(e.target.value)
            }}
            onKeyUp={e => handleInputKeyUp(e)}
          />
        </div>
        <div className='searchProductResultContent grid mb-[150px] ml-[-25px]'>
          {products.length > 0 &&
            products.map(el => (
              <SearchProductItemCard
                className='!pr-0'
                key={el._id}
                id={el._id}
                finalPrice={el.finalPrice
                  .toLocaleString(Languages.VN, {
                    style: 'currency',
                    currency: 'VND',
                  })
                  .replace(/\s₫/g, 'đ')}
                salePrice={el.price
                  .toLocaleString(Languages.VN, {
                    style: 'currency',
                    currency: 'VND',
                  })
                  .replace(/\s₫/g, 'đ')}
                name={getTranslate(el.name)}
                imgSrc={el?.thumbnail}
                imgAlt={getTranslate(el.name)}
                handleAddToCompare={() => handleAddToCompare(el)}
                canCompare={isNil(productCompares.find(f => f._id === el._id))}
              />
            ))}
          {debouncedSearchBarValue && !loading && products.length === 0 && (
            <div>
              <p className='text-center pl-[30px] mt-[10px]'>
                Không tìm thấy sản phẩm phù hợp với tìm kiếm của bạn
              </p>
            </div>
          )}
        </div>
      </div>
    </DrawBottom>
  )
}

export default ChooseProductCompare
